<template>
  <SideModal
    title="Convert Currency"
    subTitle="Transfer funds between your wallets"
    :showBackButton="false"
    @close="$emit('close')"
  >
    <template v-slot:content>
      <div class="convert-form">
        <!-- From Section -->
        <div class="convert-form__section">
          <label class="convert-form__label">From</label>
          <div class="convert-form__from-display">
            <div class="convert-form__currency-info">
              <CurrencyFlag :currency="fromCurrency" />
              <span class="convert-form__currency-name">{{ fromCurrency }} Wallet</span>
            </div>
            <span class="convert-form__available">
              Available: {{ formatAmountToDollar(availableBalance, 2, fromCurrency) }}
            </span>
          </div>
        </div>

        <!-- Amount Input -->
        <div class="convert-form__section">
          <label class="convert-form__label">Amount to Convert</label>
          <div class="convert-form__input-wrapper">
            <span class="convert-form__input-prefix">{{ getCurrencySymbol(fromCurrency) }}</span>
            <input
              type="text"
              v-model="displayAmount"
              class="convert-form__input"
              placeholder="0.00"
              @input="handleAmountInput"
              @keypress="isNumber($event)"
            />
          </div>
          <p v-if="amountError" class="convert-form__error">{{ amountError }}</p>
        </div>

        <!-- Swap Icon -->
        <div class="convert-form__swap">
          <div class="convert-form__swap-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4.16667V15.8333M10 15.8333L5.83333 11.6667M10 15.8333L14.1667 11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- To Section -->
        <div class="convert-form__section">
          <label class="convert-form__label">To</label>
          <div class="convert-form__dropdown" @click="toggleDropdown">
            <div class="convert-form__dropdown-selected" v-if="selectedWallet">
              <CurrencyFlag :currency="selectedWallet.currency" />
              <span class="convert-form__dropdown-text">{{ selectedWallet.currency }} Wallet</span>
            </div>
            <span v-else class="convert-form__dropdown-placeholder">Select target currency</span>
            <svg class="convert-form__dropdown-icon" :class="{ 'convert-form__dropdown-icon--open': showDropdown }" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            
            <div class="convert-form__dropdown-menu" v-if="showDropdown">
              <button
                v-for="wallet in wallets"
                :key="wallet.currency"
                class="convert-form__dropdown-item"
                :class="{ 'convert-form__dropdown-item--active': selectedWallet?.currency === wallet.currency }"
                @click.stop="selectWallet(wallet)"
              >
                <CurrencyFlag :currency="wallet.currency" />
                <span>{{ wallet.currency }} Wallet</span>
                <span class="convert-form__dropdown-balance">{{ formatAmountToDollar(wallet.balance, 2, wallet.currency) }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Conversion Preview -->
        <div class="convert-form__preview" v-if="selectedWallet && amount > 0 && currencyRate">
          <div class="convert-form__preview-row">
            <span class="convert-form__preview-label">Exchange Rate</span>
            <span class="convert-form__preview-value">
              {{ formatAmountToDollar(1, 2, fromCurrency) }} = {{ formatAmountToDollar(currencyRate, 4, selectedWallet.currency) }}
            </span>
          </div>
          <div class="convert-form__preview-row convert-form__preview-row--highlight">
            <span class="convert-form__preview-label">You'll Receive</span>
            <span class="convert-form__preview-value convert-form__preview-value--large">
              {{ formatAmountToDollar(convertedAmount, 2, selectedWallet.currency) }}
            </span>
          </div>
        </div>

        <!-- Action Button -->
        <div class="convert-form__actions">
          <button 
            class="convert-form__btn" 
            @click="handleConvert"
            :disabled="!canConvert || loading"
          >
            <span v-if="loading" class="convert-form__spinner"></span>
            <span v-else>Convert Currency</span>
          </button>
        </div>
      </div>
    </template>
  </SideModal>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import SideModal from "@/components/general/sidemodal/SideModal.vue";
import CurrencyFlag from "./CurrencyFlag.vue";
import { formatAmountToDollar, convertToLocale, isNumber, getCurrencySymbol } from "@/utils/helpers";

const props = defineProps({
  fromCurrency: {
    type: String,
    required: true,
  },
  availableBalance: {
    type: Number,
    required: true,
  },
  wallets: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["close", "success"]);

const store = useStore();
const amount = ref(0);
const displayAmount = ref("");
const selectedWallet = ref(null);
const showDropdown = ref(false);
const loading = ref(false);
const amountError = ref("");

// Get exchange rate from Vuex store
const currencyRate = computed(() => store.state.organisationModule.currencyRate);

const convertedAmount = computed(() => {
  if (!amount.value || !currencyRate.value) return 0;
  return Number(amount.value) * Number(currencyRate.value);
});

const canConvert = computed(() => {
  return (
    selectedWallet.value &&
    amount.value > 0 &&
    amount.value <= props.availableBalance &&
    !amountError.value &&
    currencyRate.value
  );
});

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const selectWallet = async (wallet) => {
  selectedWallet.value = wallet;
  showDropdown.value = false;
  await fetchExchangeRate();
};

const fetchExchangeRate = async () => {
  if (!selectedWallet.value) return;
  
  try {
    const payload = {
      from: props.fromCurrency,
      to: selectedWallet.value.currency,
      amount: 1,
    };
    await store.dispatch("organisationModule/convertCurrency", payload);
  } catch (error) {
    console.error("Failed to fetch exchange rate:", error);
  }
};

// getCurrencySymbol is imported from @/utils/helpers

const handleAmountInput = (event) => {
  // Remove commas to get raw value
  const rawValue = event.target.value.replace(/,/g, '');
  const numValue = parseFloat(rawValue) || 0;
  
  amount.value = numValue;
  
  // Format with commas
  if (rawValue === '' || rawValue === '0') {
    displayAmount.value = '';
  } else {
    displayAmount.value = convertToLocale(numValue);
  }
  
  calculateConversion();
};

const calculateConversion = () => {
  amountError.value = "";
  
  if (amount.value < 0) {
    amountError.value = "Amount must be positive";
    return;
  }
  
  if (amount.value > props.availableBalance) {
    amountError.value = `Amount exceeds available balance of ${formatAmountToDollar(props.availableBalance, 2, props.fromCurrency)}`;
    return;
  }
};

const handleConvert = async () => {
  if (!canConvert.value) return;
  
  loading.value = true;
  try {
    const payload = {
      amount: Number(amount.value),
      from: props.fromCurrency,
      to: selectedWallet.value.currency,
    };
    const response = await store.dispatch("organisationModule/convertWallet", payload);
    const { message } = response.data;
    store.dispatch("showToast", {
      description: message || "Conversion successful",
      display: true,
      type: "success",
    });
    await store.dispatch("organisationModule/getWalletBalance");
    emit("success");
    emit("close");
  } catch (error) {
    console.error("Conversion failed:", error);
  } finally {
    loading.value = false;
  }
};

// Close dropdown when clicking outside
const closeDropdown = (e) => {
  if (!e.target.closest('.convert-form__dropdown')) {
    showDropdown.value = false;
  }
};

// Set default selected wallet
onMounted(() => {
  if (props.wallets.length > 0) {
    selectWallet(props.wallets[0]);
  }
  document.addEventListener('click', closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<style lang="scss" scoped>
.convert-form {
  padding: 8px 24px 24px;

  &__section {
    margin-bottom: 20px;
  }

  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin-bottom: 8px;
  }

  &__from-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 14px 16px;
  }

  &__currency-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__currency-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
  }

  &__available {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: #4D84FF;
    }
  }

  &__input-prefix {
    padding: 14px 16px;
    background: #F8FAFC;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #64748B;
    border-right: 1px solid #E2E8F0;
  }

  &__input {
    flex: 1;
    padding: 14px 16px;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    outline: none;

    &::placeholder {
      color: #94A3B8;
      font-weight: 400;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__error {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #EF4444;
    margin-top: 8px;
  }

  &__swap {
    display: flex;
    justify-content: center;
    margin: 16px 0;
  }

  &__swap-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #F1F5F9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
  }

  &__dropdown {
    position: relative;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 14px 16px;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
      border-color: #4D84FF;
    }
  }

  &__dropdown-selected {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__dropdown-text {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
    flex: 1;
  }

  &__dropdown-placeholder {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 400;
    color: #94A3B8;
    flex: 1;
  }

  &__dropdown-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748B;
    transition: transform 0.2s ease;

    &--open {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  &__dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    z-index: 100;
    overflow: hidden;
  }

  &__dropdown-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: none;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    cursor: pointer;
    transition: background 0.15s ease;
    text-align: left;

    &:hover {
      background: #F8FAFC;
    }

    &--active {
      background: #EEF4FF;
      color: #4D84FF;
    }
  }

  &__dropdown-balance {
    margin-left: auto;
    font-size: 13px;
    color: #64748B;
  }

  &__preview {
    background: #F8FAFC;
    border-radius: 10px;
    padding: 16px;
    margin-top: 24px;
  }

  &__preview-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;

    &:not(:last-child) {
      border-bottom: 1px solid #E2E8F0;
    }

    &--highlight {
      padding-top: 12px;
    }
  }

  &__preview-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
  }

  &__preview-value {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;

    &--large {
      font-size: 18px;
      color: #4D84FF;
    }
  }

  &__actions {
    margin-top: 32px;
  }

  &__btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    background: #4D84FF;
    border: none;
    color: white;

    &:hover:not(:disabled) {
      background: #3B6FE8;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
